/**
 * Zentrale Animation Timing Konfiguration
 *
 * Alle Zeitangaben in Millisekunden.
 * Änderungen hier wirken sich auf alle Animationen aus.
 *
 * Kategorien:
 * - splashes: Phasen-Übergänge und Finish-Splashes
 * - endSequence: Choreographierte Rundenend-Animation
 * - midGame: Animationen während des Spiels
 * - cards: Karten-Bewegungen
 * - dealing: Karten-Austeilen Animation
 * - exchange: Kartentausch Animation
 * - roundEnd: Round End Screen Animationen
 * - victory: Victory Screen Timing
 * - effects: Effekt-Animationen (Siegel, Joker)
 * - notifications: Toast/Benachrichtigungs-Dauern
 * - transitions: UI-Übergänge
 * - minigames: Minispiel-Timings (aus shared/config für Server-Zugriff)
 */

// Minigame timings from shared config (used by both client and server)
import { MINIGAME_TIMINGS } from '../shared/config/minigameTimings';

// Magic card timings from shared config (used by both client and server)
import { MAGIC_CARD_TIMINGS } from '../shared/core/magicCardConstants';

export const ANIMATION_TIMINGS = {
  // === SPLASHES ===
  // Phasen-Übergänge und Finish-Anzeigen
  splashes: {
    phaseDuration: 1500,       // Standard-Dauer für alle Phasen-Splashes
    playerFinished: 1500,      // "X ist raus" Splash
    playerFinishedFade: 200,   // Fade-out vor Dismiss
    pendingRoundEnd: 1500,     // Pause vor Round End Screen (App.js)
  },

  // === END SEQUENCE ===
  // Choreographierte Animation wenn letzte Karte gespielt wird
  // Total für 2 Spieler: ~16s
  endSequence: {
    cardLinger: 4000,          // Karten bleiben auf dem Stapel (4s)
    finishSplash: 5000,        // Pro Spieler-Splash (5s)
    preRoundEnd: 2000,         // Pause vor Round End Screen (2s)
  },

  // === MID GAME ===
  // Animationen während das Spiel läuft
  midGame: {
    finishSplash: 3000,        // "X ist raus" während Spiel weiterläuft
    victorySplash: 3500,       // "X gewinnt!" Splash vor Victory-Phase
    trickWin: 1500,            // Stich gewonnen Animation
    frozenCards: 1500,         // Gewinnende Karten bleiben sichtbar
    jokerHighlight: 1500,      // Joker-Trigger Animation
  },

  // === CARDS ===
  // Karten-Bewegungen
  cards: {
    flyingAdded: 900,          // Karte fliegt zur Hand
    flyingRemoved: 800,        // Karte fliegt weg
    flyingOther: 700,          // Karte fliegt zu anderem Spieler
    flyingPlayed: 500,         // Karte wird gespielt
    flyingStagger: 100,        // Verzögerung zwischen mehreren Karten
    drawnCard: 1000,           // Einzelne gezogene Karte
    playingClear: 1000,        // Fallback für playing cards cleanup
  },

  // === DEALING ===
  // Karten-Austeilen Animation
  dealing: {
    cardDuration: 600,         // Animation pro Karte (flying cards)
    cardStagger: 60,           // Verzögerung zwischen Karten (flying)
    completionDelay: 200,      // Pause nach letzter Karte
    // Spread-out Animation (nach dem Fliegen)
    spreadPause: 150,          // Pause vor dem Auffächern
    spreadDuration: 500,       // Auffächer-Animation Dauer
    spreadStagger: 30,         // Verzögerung zwischen Karten beim Auffächern
    spreadSettleTime: 800,     // Zeit bis Animation komplett fertig
  },

  // === CARD SPREAD PATTERN ===
  // Natürliche Kartenverteilung für visuellen Effekt (6er-Muster)
  cardSpreadPattern: {
    rotations: [-2, 1, -1, 2, 1, -1],      // Rotation in Grad
    translateYs: [-3, 2, -1, 3, -2, 1],    // Y-Versatz in Pixel
    spreadOffsetMultiplier: 50,             // Offset-Multiplikator für Auffächer-Start
  },

  // === EXCHANGE ===
  // Kartentausch Animation
  exchange: {
    flyingDuration: 800,       // Fliegende Karte Animation
    cardStagger: 150,          // Verzögerung zwischen Karten
    completionDelay: 100,      // Pause nach Animation
    receivedToast: 3000,       // Toast für erhaltene Karten
  },

  // === ROUND END ===
  // Round End Screen Animationen
  roundEnd: {
    initialDelay: 500,         // Pause vor erster Zeile
    rowDelay: 300,             // Verzögerung zwischen Zeilen
    rowStagger: 100,           // Stagger für Coins/Standings
  },

  // === VICTORY ===
  // Victory Screen Timings
  victory: {
    overviewDuration: 5000,    // (Legacy) Übersicht Phase
    statisticsDuration: 8000,  // (Legacy) Statistik Phase
    awardsDuration: 6000,      // (Legacy) Auszeichnungen Phase
    xpBarAnimation: 1500,      // XP-Balken Transition-Dauer
    levelUpItemStagger: 150,   // Stagger zwischen Item-Reveals
  },

  // === EFFECTS ===
  // Siegel und Joker Animationen
  effects: {
    cardBadge: 2000,           // Seal-Effect Badge
    jokerBonus: 200,           // Verzögerung für Joker-Bonus Sound
    sealTransition: 200,       // Siegel Hover/Click Transition
    sealPop: 500,              // Siegel "Pop" Animation
    sealSlideIn: 400,          // Siegel Slide-In Animation
    sealSlideInStagger: 100,   // Stagger zwischen Siegeln
    sealLegendaryPulse: 2000,  // Legendäres Siegel Pulsieren
  },

  // === NOTIFICATIONS ===
  // Toast und Benachrichtigungen
  notifications: {
    default: 2000,             // Standard Toast Dauer
    effectToast: 2500,         // Effekt-Ankündigung Toast
    disconnectWarning: 5000,   // Spieler getrennt Warnung
    reconnectSuccess: 3000,    // Spieler wieder verbunden
    turnTimeout: 3000,         // Turn Timeout Benachrichtigung
    turnTimeoutOther: 2000,    // Turn Timeout anderer Spieler
  },

  // === TRANSITIONS ===
  // UI-Übergänge und sonstige Delays
  transitions: {
    scoreAnimation: 500,       // Score Update Animation
    floatingScore: 1500,       // Schwebende Punktzahl
    playerInfoChange: 1000,    // Spieler-Info Änderung Highlight
    controlsScrollSync: 600,   // Scroll-Sync mit Fade-In
  },

  // === MINIGAMES ===
  // Minispiel-bezogene Timings (aus shared/config für Server-Zugriff)
  minigames: MINIGAME_TIMINGS,

  // === MAGIC CARDS ===
  // Zauberkarten-bezogene Timings (aus shared/core für Server-Zugriff)
  magicCards: MAGIC_CARD_TIMINGS,
};

// === CONVENIENCE EXPORTS ===
// Häufig genutzte Kategorien als separate Exports
export const SPLASHES = ANIMATION_TIMINGS.splashes;
export const END_SEQUENCE = ANIMATION_TIMINGS.endSequence;
export const MID_GAME = ANIMATION_TIMINGS.midGame;
export const CARDS = ANIMATION_TIMINGS.cards;
export const DEALING = ANIMATION_TIMINGS.dealing;
export const CARD_SPREAD_PATTERN = ANIMATION_TIMINGS.cardSpreadPattern;
export const EXCHANGE = ANIMATION_TIMINGS.exchange;
export const ROUND_END = ANIMATION_TIMINGS.roundEnd;
export const VICTORY = ANIMATION_TIMINGS.victory;
export const EFFECTS = ANIMATION_TIMINGS.effects;
export const NOTIFICATIONS = ANIMATION_TIMINGS.notifications;
export const TRANSITIONS = ANIMATION_TIMINGS.transitions;
export const MINIGAMES = ANIMATION_TIMINGS.minigames;
export const MAGIC_CARDS_TIMING = ANIMATION_TIMINGS.magicCards;

export default ANIMATION_TIMINGS;
